import { Door, Couch, CookingPot, Bed, Shower, Toilet, Steps, Gear, Package, ForkKnife,
  CheckCircle, Camera, ArrowLeft, List, Lightning, LightningSlash, Images, Stack } from "@phosphor-icons/react";
import { roomPhotos, roomLabel } from "./walkApi";
import FieldRadarLogo from "./FieldRadarLogo";

const ICONS = { door: Door, sofa: Couch, kitchen: CookingPot, bed: Bed, shower: Shower, toilet: Toilet,
  stairs: Steps, gear: Gear, box: Package, dining: ForkKnife };

export function RoomIcon({ icon, size = 26, className = "" }) {
  const I = ICONS[icon] || Package;
  return <I weight="fill" size={size} className={className}/>;
}

const PILL = {
  done: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  prog: "bg-teal-500/20 text-teal-200 border-teal-400/40",
  wait: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};
const PILL_LABEL = { done: "Done", prog: "In progress", wait: "Not started" };

export default function RoomListScreen({ data, mode, setMode, staff, onOpen, onBack, onFinish, notifyCard, unsortedCount = 0, onImport, onSort }) {
  const rooms = data.rooms || [];
  const done = rooms.filter(r => r.status === "done").length;
  const allDone = rooms.length > 0 && done === rooms.length;
  const kindLabel = { move_in: "Move-in", move_out: "Move-out", periodic: "Periodic" }[data.kind] || "Walk";
  return (
    <div className="min-h-screen bg-[#0b1c2d] text-white pb-28" data-testid="walk-room-list">
      <div className="sticky top-0 z-20 bg-[#0b1c2d]/95 backdrop-blur px-4 pt-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full active:bg-white/10" data-testid="walk-back">
            <ArrowLeft weight="bold" size={26} className="text-teal-400"/>
          </button>
          <div className="min-w-0 flex-1">
            <FieldRadarLogo size={22} sub={staff ? "Inspect Radar" : "Tenant Radar"} className="mb-1.5"/>
            <div className="font-black text-xl leading-tight truncate" data-testid="walk-address">{data.property_address}{data.unit_id ? ` #${data.unit_id}` : ""}</div>
            <div className="text-sm text-slate-300">{kindLabel} · <span data-testid="walk-rooms-done">{done} of {rooms.length} rooms done</span></div>
          </div>
        </div>
        {staff && (
          <div className="mt-3 grid grid-cols-2 gap-1 p-1 bg-white/5 rounded-xl" data-testid="walk-mode-toggle">
            {[["guided", "Guided", "Room → chips → photos"], ["fast", "Fast Track", "One photo per room · flag problems"]].map(([k, l, sub]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`walk-mode-${k}`}
                className={`h-12 rounded-lg text-left px-3 transition-colors ${mode === k ? "bg-teal-500 text-slate-900" : "text-slate-300 active:bg-white/10"}`}>
                <div className="font-black text-sm leading-none">{l}</div>
                <div className={`text-[10px] leading-tight mt-0.5 ${mode === k ? "text-slate-800" : "text-slate-400"}`}>{sub}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {staff && data.walker === "tenant" && data.tenant_link_url && (
        <div className="mx-4 mt-3 flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm" data-testid="walk-tenant-link-row">
          <div className="flex-1"><b>Tenant walk</b> <span className="text-slate-400">— link ready, camera only</span></div>
          <button onClick={() => { navigator.clipboard?.writeText(data.tenant_link_url); }} className="h-9 px-3 rounded-lg bg-teal-500 text-slate-900 font-black text-xs" data-testid="walk-copy-tenant-link">Copy link</button>
        </div>
      )}
      {staff && (
        <div className="mx-4 mt-3 flex gap-2" data-testid="walk-import-row">
          <button onClick={onImport} className="flex-1 h-12 rounded-xl bg-white/10 border border-white/10 font-bold text-sm flex items-center justify-center gap-2" data-testid="walk-import-photos">
            <Images weight="fill" size={20} className="text-teal-300"/> Import photos
          </button>
          {unsortedCount > 0 && (
            <button onClick={onSort} className="flex-1 h-12 rounded-xl bg-teal-500 text-slate-900 font-black text-sm flex items-center justify-center gap-2" data-testid="walk-sort-photos">
              <Stack weight="fill" size={20}/> Sort {unsortedCount} unsorted
            </button>
          )}
        </div>
      )}
      {notifyCard}

      <div className="px-4 pt-3 space-y-2.5">
        {rooms.map((r, idx) => {
          const n = roomPhotos(r).length;
          const st = r.status || "wait";
          return (
            <button key={idx} onClick={() => onOpen(idx)} data-testid={`walk-room-${idx}`}
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-[#13263a] border border-white/10 text-left active:scale-[0.99] active:bg-[#1a3149] transition-transform">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${st === "done" ? "bg-emerald-500/20 text-emerald-300" : st === "prog" ? "bg-teal-500/20 text-teal-300" : "bg-white/10 text-slate-200"}`}>
                <RoomIcon icon={r.icon} size={30}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-black text-lg leading-tight truncate">{roomLabel(r)}</div>
                <div className="text-sm text-slate-400 flex items-center gap-1.5 mt-0.5">
                  <Camera weight="fill" size={14}/> {n} photo{n === 1 ? "" : "s"}
                  {r.fast_flag && <span className="ml-1 text-red-300 font-bold">· flagged</span>}
                  {r.note && <span className="ml-1 text-teal-300 font-bold">· note</span>}
                  {r.override && <span className="ml-1 text-yellow-300 font-bold">· override</span>}
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-black border whitespace-nowrap ${PILL[st]}`} data-testid={`walk-room-status-${idx}`}>{PILL_LABEL[st]}</span>
            </button>
          );
        })}
      </div>

      <div className="fixed bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#0b1c2d] via-[#0b1c2d] to-transparent">
        <button onClick={onFinish} disabled={!allDone && !staff} data-testid="walk-finish"
          className={`w-full h-14 rounded-2xl font-black text-lg flex items-center justify-center gap-2 shadow-lg ${allDone ? "bg-teal-500 text-slate-900 shadow-teal-500/20" : staff ? "bg-white/10 text-slate-200 border border-white/15" : "bg-white/10 text-slate-400"}`}>
          <CheckCircle weight="fill" size={24}/> {allDone ? "Finish walk" : staff ? `Finish anyway · ${rooms.length - done} left` : `${rooms.length - done} room${rooms.length - done === 1 ? "" : "s"} left`}
        </button>
      </div>
    </div>
  );
}

export { Lightning, LightningSlash, List };
